// "Anschrift einfuegen": eine eingefuegte Anschrift regelbasiert vorschlagen
// (ST-PTY-001 Schnellerfassung, danach ST-PTY-002 Adressseite; beitragend
// CM-PTY-006, CM-CAP-001; NFR-UX-003..005, NFR-ACC-001; ADR-0056).
//
// EIN VORSCHLAG UND KEIN ERGEBNIS. Dieses Modul erkennt nichts sicher. Es
// liest aus einer Signatur, einer Visitenkarte oder einer E-Mail-Fusszeile die
// wahrscheinliche Anschrift heraus und legt sie in die Felder, die die Nutzerin
// ohnehin sieht und korrigieren kann. Gespeichert wird erst, was sie bestaetigt,
// und zwar WIE EINGEGEBEN (ADR-0056 Ziffer 2): die Zeilen gehen wortgetreu in
// die Adresszeilen, Strasse und Hausnummer werden zum Erkennen getrennt, aber
// nicht getrennt gespeichert -- `street_name` und `house_number` behaupteten
// eine Zerlegung, die niemand geprueft hat.
//
// NUR DEUTSCHLAND, OESTERREICH UND DIE SCHWEIZ, und nur ueber ihre Ortszeile
// "PLZ Ort". Sie ist der eine Anker, an dem sich eine Anschrift in freiem Text
// verlaesslich erkennen laesst; alles andere haengt an ihr. Eine Anschrift
// ohne erkennbare Ortszeile ergibt KEINEN Vorschlag, und der Text bleibt
// unveraendert -- ein halber Vorschlag waere schlechter als keiner.
//
// DIE PLZ ALLEIN ENTSCHEIDET DAS LAND NUR BEI FUENF ZIFFERN. Vier Ziffern hat
// Oesterreich UND die Schweiz; ohne Praefix ("A-", "CH-") oder Laenderwort
// bleibt das Land LEER, und ein Hinweis sagt warum. Eine Vorbelegung
// "Deutschland" waere hier eine falsche Anschrift, die niemand bemerkt, weil
// der Server ausserhalb von DE keine PLZ prueft (A2 vom 28.09.2026).
//
// WAS UEBRIG BLEIBT, VERSCHWINDET NICHT (dasselbe Versprechen wie free-text.ts):
// `remainingText` ist die Eingabe OHNE die verbrauchten Zeilen, und genau sie
// geht in der Schnellerfassung an `parseFreeText`. Name, Firma, E-Mail und
// Telefon werden dort erkannt, alles andere erscheint dort als "nicht
// zugeordnet". Eine Zeile, die zur Anschrift gehoeren koennte, aber nicht
// sicher genug ist, wird nicht verbraucht -- sie bleibt im Rest sichtbar. Eine
// ERKANNTE, aber nicht uebernommene Zeile (zweite Strasse, zweite Ortszeile)
// steht genau einmal da: als Hinweis mit Wortlaut in `notes`, nicht zusaetzlich
// im Rest -- zweimal dieselbe Zeile liesse raten, welche Aussage gilt.
//
// WARUM REGELN UND KEIN MODELL: dieselben vier Gruende wie im Kopf von
// free-text.ts (kein Anbieter, keine personenbezogenen Daten an Dritte,
// deterministisch pruefbar, sofort).
//
// KEINE ANSCHRIFT VERLAESST DIESES MODUL anders als ueber seinen
// Rueckgabewert: kein Log, keine Telemetrie (NFR-PRV-001, ADR-0056 Ziffer 2).

import type { FreeTextDraft, UnassignedFragment } from './free-text.ts';

/**
 * Laengengrenzen -- DIESELBEN wie in apps/api/src/domain/party/address-commands.ts,
 * gehalten durch address-paste.test.ts. Ein Vorschlag, den der Server
 * ablehnen muesste, waere eine Sackgasse.
 */
export const MAXIMUM_PROPOSED_LINE_LENGTH = 200;
export const MAXIMUM_PROPOSED_LOCALITY_LENGTH = 100;

/** Die Laender, die dieser Zerleger kennt. */
export type ProposedCountry = 'DE' | 'AT' | 'CH';

/** Was der Zerleger vorschlaegt. Leere Zeichenkette heisst: nicht vorgeschlagen. */
export interface AddressProposal {
  /** '' wenn das Land nicht eindeutig ist -- dann steht ein Hinweis in `notes`. */
  readonly countryCode: ProposedCountry | '';
  readonly addressLine1: string;
  readonly addressLine2: string;
  readonly postalCode: string;
  readonly locality: string;
}

export type AddressNoteReason =
  /** Vierstellige PLZ ohne Praefix und ohne Laenderwort. */
  | 'country_ambiguous'
  /** Praefix, Laenderwort und PLZ widersprechen sich. */
  | 'country_conflict'
  /** Ueber der Ortszeile stand keine erkennbare Strasse und kein Postfach. */
  | 'street_missing'
  /** Eine zweite Strassen- oder Postfachzeile -- uebernommen ist die naeher an der Ortszeile. */
  | 'second_line_not_taken'
  /** Eine zweite Ortszeile -- uebernommen ist die erste. */
  | 'second_locality_not_taken';

export interface AddressNote {
  /** Der Wortlaut aus der Eingabe, falls der Hinweis an einer Zeile haengt. */
  readonly text?: string;
  readonly reason: AddressNoteReason;
}

export interface AddressPasteResult {
  /** Fehlt, wenn keine Ortszeile erkannt wurde. */
  readonly proposal?: AddressProposal;
  /** In Lesereihenfolge. */
  readonly notes: readonly AddressNote[];
  /** Die Eingabe ohne die verbrauchten Zeilen und Abschnitte. Ohne Vorschlag: die Eingabe selbst. */
  readonly remainingText: string;
}

/** Die Hinweise in Fachsprache. Keiner nennt einen Fehler -- es sind Vorschlaege. */
export const ADDRESS_NOTE_TEXTS: Readonly<Record<AddressNoteReason, string>> = Object.freeze({
  country_ambiguous: 'Die vierstellige Postleitzahl passt zu Österreich und zur Schweiz. Wähle das Land.',
  country_conflict: 'Postleitzahl und Land passen nicht zusammen. Wähle das Land.',
  street_missing: 'Straße und Hausnummer wurden nicht erkannt. Trage sie ein.',
  second_line_not_taken: 'ist eine weitere Anschriftzeile und wurde nicht übernommen',
  second_locality_not_taken: 'ist eine zweite Anschrift und wurde nicht übernommen',
});

/**
 * Dieselben Trenner wie free-text.ts: Zeilenumbruch, Semikolon, senkrechter Strich,
 * Tabulator, Mittelpunkt und Aufzaehlungspunkt, dazu der Gedankenstrich mit Leerzeichen.
 * Signaturen setzen Organisation, Strasse und Ort oft in eine Zeile ("Muster Software
 * GmbH · Beispielstr. 6 · 30159 Hannover", X5); ohne Leerzeichen bleibt der
 * Gedankenstrich Teil des Worts ("Hamburg–Altona").
 */
const LINE_SEPARATOR = /[\n\r;|\t·•]+|\s+–\s+/u;

/**
 * Die Ortszeile. Praefix optional; die Stellenzahl muss zum Praefix passen,
 * sonst ist es keine Ortszeile (ein "D-1010" ist kein Ort, sondern ein Irrtum,
 * und ein Irrtum wird nicht vorgeschlagen). Der Ort beginnt mit einem
 * Buchstaben und traegt nur, was Ortsnamen tragen: "Frankfurt am Main",
 * "Halle (Saale)", "St. Gallen", "Hamburg-Altona".
 */
const LOCALITY_PATTERN =
  /^(?:(D|DE|A|AT|CH)\s?[-–]\s?)?(\d{4,5})\s+(\p{L}[\p{L}\p{M} .'’()\/-]*[\p{L}\p{M}.)])$/u;

const PREFIX_COUNTRY: Readonly<Record<string, ProposedCountry>> = Object.freeze({
  D: 'DE',
  DE: 'DE',
  A: 'AT',
  AT: 'AT',
  CH: 'CH',
});

const POSTAL_CODE_DIGITS: Readonly<Record<ProposedCountry, number>> = Object.freeze({ DE: 5, AT: 4, CH: 4 });

/** Laenderwoerter, als EIGENER Abschnitt -- nie als Teil eines Ortsnamens. */
const COUNTRY_WORDS: Readonly<Record<string, ProposedCountry>> = Object.freeze({
  deutschland: 'DE',
  germany: 'DE',
  de: 'DE',
  österreich: 'AT',
  oesterreich: 'AT',
  austria: 'AT',
  at: 'AT',
  schweiz: 'CH',
  suisse: 'CH',
  svizzera: 'CH',
  switzerland: 'CH',
  ch: 'CH',
});

/**
 * Strasse mit Hausnummer. Die Hausnummer darf einen Buchstaben tragen ("12a",
 * "12 a"), einen Bereich ("12-14", "12–14") oder die oesterreichische Folge
 * aus Stiege und Tuer ("12/3/5"). "Straße des 17. Juni 135" ist eine Strasse:
 * Ziffern im Namen sind erlaubt, die LETZTE Zahl ist die Hausnummer.
 */
const STREET_PATTERN =
  /^(\p{L}[\p{L}\p{M}0-9 .'’\/-]*?)(?:\s+|(?<=\.)\s*)(\d{1,4}(?:\s?[a-zA-Z])?(?:\s?[-–\/]\s?\d{1,4}(?:\s?[a-zA-Z])?)*)$/u;

/** Was wie eine Strasse aussieht, aber eine Rufnummer mit Vorsatz ist ("Tel. 040 1234"). */
const NOT_A_STREET_PATTERN = /^(tel|telefon|fon|fax|mobil|mob|handy|phone|durchwahl|zentrale)\b/iu;

const PO_BOX_PATTERN = /^postfach(?:\s+[0-9][0-9 ]*)?$/iu;
const CARE_OF_PATTERN = /^c\s?\/\s?o\s+\S/iu;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f-\u009f\u2028\u2029\u202a-\u202e\u2066-\u2069]/u;

interface Segment {
  readonly line: number;
  readonly index: number;
  readonly text: string;
}

interface LocalityFind {
  readonly segment: Segment;
  readonly prefixCountry?: ProposedCountry;
  readonly postalCode: string;
  readonly locality: string;
}

function localityOf(segment: Segment): LocalityFind | undefined {
  const match = LOCALITY_PATTERN.exec(segment.text);
  if (match === null) {
    return undefined;
  }
  const [, prefix, postalCode = '', locality = ''] = match;
  const prefixCountry = prefix === undefined ? undefined : PREFIX_COUNTRY[prefix];
  if (prefixCountry !== undefined && POSTAL_CODE_DIGITS[prefixCountry] !== postalCode.length) {
    return undefined;
  }
  if (locality.length > MAXIMUM_PROPOSED_LOCALITY_LENGTH) {
    return undefined;
  }
  return { segment, postalCode, locality, ...(prefixCountry === undefined ? {} : { prefixCountry }) };
}

function isStreet(text: string): boolean {
  if (text.length > MAXIMUM_PROPOSED_LINE_LENGTH || NOT_A_STREET_PATTERN.test(text)) {
    return false;
  }
  const match = STREET_PATTERN.exec(text);
  // Mindestens drei Buchstaben im Namen: "A 5" ist keine Strasse.
  return match !== null && (match[1] ?? '').replace(/[^\p{L}]/gu, '').length >= 3;
}

function isPoBox(text: string): boolean {
  return PO_BOX_PATTERN.test(text) && text.length <= MAXIMUM_PROPOSED_LINE_LENGTH;
}

function isCareOf(text: string): boolean {
  return CARE_OF_PATTERN.test(text) && text.length <= MAXIMUM_PROPOSED_LINE_LENGTH;
}

function countryWordOf(text: string): ProposedCountry | undefined {
  return COUNTRY_WORDS[text.toLocaleLowerCase('de-DE')];
}

/** Die Abschnitte einer Zeile, getrennt am Komma -- "Hauptstr. 5, 20095 Hamburg". */
function segmentsOf(lines: readonly string[]): Segment[][] {
  return lines.map((line, lineIndex) =>
    line
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .map((text, index) => ({ line: lineIndex, index, text })),
  );
}

/** Die Abschnitte VOR `segment`, naechster zuerst, ueber Zeilengrenzen hinweg. */
function segmentsBefore(all: readonly (readonly Segment[])[], segment: Segment): Segment[] {
  const before: Segment[] = [];
  for (let line = segment.line; line >= 0; line -= 1) {
    const row = all[line] ?? [];
    for (let index = row.length - 1; index >= 0; index -= 1) {
      const candidate = row[index];
      if (candidate !== undefined && (line < segment.line || candidate.index < segment.index)) {
        before.push(candidate);
      }
    }
  }
  return before;
}

/** Der Abschnitt direkt NACH `segment`, ueber Zeilengrenzen hinweg. */
function segmentAfter(all: readonly (readonly Segment[])[], segment: Segment): Segment | undefined {
  for (let line = segment.line; line < all.length; line += 1) {
    for (const candidate of all[line] ?? []) {
      if (line > segment.line || candidate.index > segment.index) {
        return candidate;
      }
    }
  }
  return undefined;
}

function resolveCountry(
  find: LocalityFind,
  wordCountry: ProposedCountry | undefined,
): { readonly countryCode: ProposedCountry | ''; readonly note?: AddressNoteReason } {
  const digits = find.postalCode.length;
  const named = [find.prefixCountry, wordCountry].filter((value): value is ProposedCountry => value !== undefined);
  if (named.length > 0) {
    const [first] = named;
    if (first === undefined || named.some((value) => value !== first) || POSTAL_CODE_DIGITS[first] !== digits) {
      return { countryCode: '', note: 'country_conflict' };
    }
    return { countryCode: first };
  }
  if (digits === 5) {
    return { countryCode: 'DE' };
  }
  return { countryCode: '', note: 'country_ambiguous' };
}

const key = (segment: Segment): string => `${segment.line}:${segment.index}`;

/**
 * Schlaegt aus freiem Text eine Anschrift vor.
 *
 * Reihenfolge: die ERSTE Ortszeile ist der Anker; der Abschnitt direkt darueber
 * ist Strasse oder Postfach (Zeile 1), ein "c/o" darueber Zeile 2; ein
 * Laenderwort direkt darunter bestimmt das Land mit. Alles Verbrauchte fehlt in
 * `remainingText`, alles andere steht dort wortgleich.
 */
export function parseAddressText(text: string): AddressPasteResult {
  const lines = text.normalize('NFC').split(LINE_SEPARATOR).map((line) => line.trim());
  const all = segmentsOf(lines);

  const localities: LocalityFind[] = [];
  for (const row of all) {
    for (const segment of row) {
      if (CONTROL_CHARACTER_PATTERN.test(segment.text)) {
        continue;
      }
      const find = localityOf(segment);
      if (find !== undefined) {
        localities.push(find);
      }
    }
  }
  const [anchor, ...otherLocalities] = localities;
  if (anchor === undefined) {
    return { notes: [], remainingText: text };
  }

  const consumed = new Set<string>([key(anchor.segment)]);
  const notes: AddressNote[] = [];

  let addressLine1 = '';
  let addressLine2 = '';
  const before = segmentsBefore(all, anchor.segment);
  const [nearest, ...further] = before;
  if (nearest !== undefined && (isStreet(nearest.text) || isPoBox(nearest.text))) {
    addressLine1 = nearest.text;
    consumed.add(key(nearest));
    // Direkt darueber: ein c/o wird Zeile 2, eine weitere Strasse oder ein
    // Postfach bleibt sichtbar im Rest -- die Ortszeile gehoert zu genau einer.
    const [above] = further;
    if (above !== undefined && isCareOf(above.text)) {
      addressLine2 = above.text;
      consumed.add(key(above));
    } else if (above !== undefined && (isStreet(above.text) || isPoBox(above.text))) {
      notes.push({ text: above.text, reason: 'second_line_not_taken' });
      consumed.add(key(above));
    }
  } else {
    notes.push({ reason: 'street_missing' });
  }

  const after = segmentAfter(all, anchor.segment);
  const wordCountry = after === undefined ? undefined : countryWordOf(after.text);
  if (after !== undefined && wordCountry !== undefined) {
    consumed.add(key(after));
  }
  const country = resolveCountry(anchor, wordCountry);
  if (country.note !== undefined) {
    notes.push({ reason: country.note });
  }
  for (const other of otherLocalities) {
    notes.push({ text: other.segment.text, reason: 'second_locality_not_taken' });
    consumed.add(key(other.segment));
  }

  const remainingText = all
    .map((row, lineIndex) => {
      if (!row.some((segment) => consumed.has(key(segment)))) {
        return lines[lineIndex] ?? '';
      }
      return row
        .filter((segment) => !consumed.has(key(segment)))
        .map((segment) => segment.text)
        .join(', ');
    })
    .filter((line) => line.length > 0)
    .join('\n');

  return {
    proposal: {
      countryCode: country.countryCode,
      addressLine1,
      addressLine2,
      postalCode: anchor.postalCode,
      locality: anchor.locality,
    },
    notes,
    remainingText,
  };
}

/** Der Satz eines Hinweises, mit dem Wortlaut, falls er an einer Zeile haengt. */
export function addressNoteText(note: AddressNote): string {
  const sentence = ADDRESS_NOTE_TEXTS[note.reason];
  return note.text === undefined ? sentence : `„${note.text}“ ${sentence}`;
}

// ---------------------------------------------------------------------------
// Der Weg in die Schnellerfassung (ST-PTY-001)
// ---------------------------------------------------------------------------

/** Kein Vorschlag -- fuer eine abgelehnte Anschrift und den leeren Freitext. */
export const NO_ADDRESS: AddressPasteResult = Object.freeze({ notes: Object.freeze([]), remainingText: '' });

/**
 * Zerlegt den Freitext in Anschrift und Rest und gibt den Rest an
 * `parseFreeText` weiter.
 *
 * `dismissed`: die Nutzerin hat "Anschrift nicht uebernehmen" gewaehlt. Dann
 * wird der GANZE Text an den Freitext gegeben -- die Anschriftzeilen stehen
 * wieder unter "Nicht zugeordnet" und verschwinden nicht mit dem Abschnitt.
 *
 * Erkannte, aber nicht uebernommene Zeilen (zweite Strasse, zweite Ortszeile)
 * erscheinen EINMAL, in derselben Liste wie der uebrige Rest und mit dem
 * Grund "zweite Angabe derselben Art" -- eine zweite Liste fuer dieselbe
 * Aussage waere eine zweite Stelle, an der die Nutzerin suchen muss. Die
 * eigenen Saetze `second_line_not_taken`/`second_locality_not_taken` sind
 * deshalb die Auskunft des ZERLEGERS (fuer die Adressseite, ST-PTY-002) und
 * erscheinen in der Schnellerfassung nicht.
 */
export function recognizeWithAddress(
  text: string,
  parse: (text: string) => FreeTextDraft,
  dismissed: boolean,
): { readonly paste: AddressPasteResult; readonly draft: FreeTextDraft } {
  const paste = dismissed || text.trim().length === 0 ? NO_ADDRESS : parseAddressText(text);
  if (paste.proposal === undefined) {
    return { paste, draft: parse(text) };
  }
  const parsed = parse(paste.remainingText);
  const notTaken: UnassignedFragment[] = paste.notes.flatMap((note) =>
    note.text === undefined ? [] : [{ text: note.text, reason: 'already_captured' as const }],
  );
  return { paste, draft: { ...parsed, unassigned: [...parsed.unassigned, ...notTaken] } };
}

/**
 * Woran eine vorgeschlagene Anschrift wiedererkannt wird: der GANZE Vorschlag
 * -- Land, beide Zeilen, PLZ und Ort.
 *
 * "Anschrift nicht uebernehmen" gilt fuer DIESE Anschrift und nicht fuer jede
 * kuenftige. Ersetzt die Nutzerin den Freitext in einem Zug durch eine andere
 * Signatur, ist die neue Anschrift ein neuer Vorschlag (Befund des
 * Experience-Reviews vom 29.09.2026: sonst eine stille Sackgasse). Nur die
 * Ortszeile genuegt nicht: "Hauptstr. 1" und "Nebenstr. 2" in 20095 Hamburg
 * sind zwei Anschriften (Codex-Cloud-Befund an #280).
 */
export function addressAnchor(paste: AddressPasteResult): string | undefined {
  const proposal = paste.proposal;
  return proposal === undefined
    ? undefined
    : JSON.stringify([proposal.countryCode, proposal.addressLine1, proposal.addressLine2, proposal.postalCode, proposal.locality]);
}

/**
 * Der Hinweis zu einem Feld der Anschrift, oder keiner: das mehrdeutige Land
 * gehoert an das Feld "Land", die fehlende Strasse an "Strasse und
 * Hausnummer" -- dort, wo die Nutzerin handelt, und nicht in einem eigenen
 * Block daneben.
 */
export function addressFieldHint(paste: AddressPasteResult, field: 'countryCode' | 'addressLine1'): string | undefined {
  const reasons: readonly AddressNoteReason[] =
    field === 'countryCode' ? ['country_ambiguous', 'country_conflict'] : ['street_missing'];
  const note = paste.notes.find((entry) => entry.text === undefined && reasons.includes(entry.reason));
  return note === undefined ? undefined : ADDRESS_NOTE_TEXTS[note.reason];
}

export const ADDRESS_SECTION_HEADING = 'Geschäftsanschrift';
export const ADDRESS_SECTION_EXPLANATION =
  'Aus dem Freitext übernommen. Sie wird so, wie sie hier steht, mit dem Kontakt als Geschäftsanschrift gespeichert.';
export const ADDRESS_DISMISS_LABEL = 'Anschrift nicht übernehmen';
/** An der Stelle der Anschrift, nachdem sie abgelehnt ist; der Knopf nimmt die Ablehnung zurueck (DESIGN B9). */
export const ADDRESS_DISMISSED = 'Anschrift nicht übernommen.';
export const ADDRESS_RESTORE_LABEL = 'Doch übernehmen';
export const ADDRESS_CHOOSE_COUNTRY = 'Bitte wählen';
export const ADDRESS_SAVED =
  'Die Geschäftsanschrift ist gespeichert.';
export const ADDRESS_SAVED_LINK = 'Anschriften dieses Kontakts';
export const ADDRESS_ERRORS_NEXT_STEP =
  'Die Anschrift braucht noch Angaben. Ergänze sie oder wähle „Anschrift nicht übernehmen“ — der Kontakt lässt sich auch ohne sie anlegen.';

/**
 * Die Zusammenfassung der Erkennung MIT Anschrift. Die Anschrift gehoert zu
 * dem, was vor der Speicherung gezeigt werden muss (CM-CAP-001) -- ohne diesen
 * Satz hiesse ein Text, der nur eine Anschrift enthaelt, "liess sich nichts
 * zuordnen", waehrend darunter die Anschrift steht.
 */
export function recognitionSummaryWithAddress(
  summary: string,
  draft: FreeTextDraft,
  nothingRecognized: string,
  found = 'Geschäftsanschrift, unten zum Prüfen.',
): string {
  if (draft.recognized.length === 0) {
    const rest = summary.startsWith(nothingRecognized) ? summary.slice(nothingRecognized.length) : '';
    return `Erkannt: ${found}${rest}`;
  }
  return `${summary} Dazu: ${found}`;
}

// ---------------------------------------------------------------------------
// Der Weg in die Adressseite (ST-PTY-002, "Anschrift einfuegen" S1)
// ---------------------------------------------------------------------------

/** Die Felder der Adressseite, die ein Vorschlag vorbefuellen darf -- der Zweck gehoert NICHT dazu. */
export type PastedAddressField = 'countryCode' | 'addressLine1' | 'addressLine2' | 'postalCode' | 'locality';

export const PASTED_ADDRESS_FIELDS: readonly PastedAddressField[] = Object.freeze([
  'countryCode',
  'addressLine1',
  'addressLine2',
  'postalCode',
  'locality',
]);

/**
 * Legt einen Vorschlag in die Eingabe der Adressseite.
 *
 * NUR IN FELDER, DIE DIE NUTZERIN NICHT ANGEFASST HAT -- und zwar JE FELD,
 * anders als die Schnellerfassung (ein Kennzeichen fuer die ganze Anschrift).
 * Der Grund ist das Land: bei einer vierstelligen PLZ MUSS die Nutzerin es
 * waehlen. Mit einem Gesamtkennzeichen taete danach jedes weitere Einfuegen
 * stumm nichts; so bleibt das gewaehlte Land stehen, und die uebrigen Felder
 * folgen dem Text (Dirigent -42, 29.09.2026).
 *
 * Ohne Vorschlag bleibt die Eingabe, wie sie ist -- ein Text ohne erkennbare
 * Ortszeile leert keine Felder. Der Zweck wird nie gesetzt (ADR-0056 Ziffer 4).
 * Ein leeres Land kommt NUR aus dem Zerleger (vierstellige PLZ, Widerspruch);
 * wer tippt, statt einzufuegen, behaelt die Vorbelegung.
 */
export function applyAddressProposal<T extends Readonly<Record<PastedAddressField, string>>>(
  current: T,
  proposal: AddressProposal | undefined,
  touched: ReadonlySet<PastedAddressField>,
): T {
  if (proposal === undefined) {
    return current;
  }
  const next: Record<string, string> = { ...current };
  for (const field of PASTED_ADDRESS_FIELDS) {
    if (!touched.has(field)) {
      next[field] = proposal[field];
    }
  }
  return next as T;
}

/**
 * Was vom eingefuegten Text NICHT in die Felder ging, je Zeile ein Satz: die
 * uebrigen Zeilen und die erkannten, aber nicht uebernommenen Zeilen mit
 * ihrem Grund. Ohne Vorschlag leer -- dann gilt ADDRESS_PASTE_NONE, und der
 * ganze Text steht ohnehin noch im Feld.
 */
export function addressPasteRest(paste: AddressPasteResult): readonly string[] {
  if (paste.proposal === undefined) {
    return [];
  }
  const rest = paste.remainingText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `„${line}“ ${ADDRESS_PASTE_REST_REASON}`);
  const notes = paste.notes.flatMap((note) => (note.text === undefined ? [] : [addressNoteText(note)]));
  return [...rest, ...notes];
}

/**
 * Bis zu dieser Laenge zerlegt die Adressseite den eingefuegten Text. Eine
 * Signatur ist wenige hundert Zeichen lang; eine uebergrosse Zwischenablage
 * wird NICHT abgeschnitten (sie bleibt sichtbar im Feld), aber auch nicht bei
 * jedem Tastendruck zerlegt (Hinweis des Codex-Erstreviews, 29.09.2026).
 */
export const MAXIMUM_PASTE_LENGTH = 2000;

export const ADDRESS_PASTE_LABEL = 'Anschrift einfügen';
export const ADDRESS_PASTE_HINT =
  'Zum Beispiel aus einer E-Mail-Signatur. Die Felder darunter werden vorbefüllt — prüfe sie und wähle den Zweck. Der eingefügte Text selbst wird nicht gespeichert.';
export const ADDRESS_PASTE_NONE =
  'Hier wurde keine Anschrift erkannt. Erkannt werden Anschriften aus Deutschland, Österreich und der Schweiz mit Postleitzahl und Ort. Trage die Anschrift sonst unten ein.';
export const ADDRESS_PASTE_TOO_LONG =
  'Der Text ist zu lang, um darin eine Anschrift zu erkennen. Füge nur die Anschrift oder die Signatur ein.';
/**
 * Die Meldung des Einfuegens fuer die EINE Statusregion der Adressseite --
 * damit auch, wer mit Screenreader im Textfeld bleibt, erfaehrt, was
 * uebernommen wurde und was nicht (Codex-Cloud-Befund P1 an #286). Keine
 * Meldung fuer einen leeren Text.
 */
export function addressPasteAnnouncement(paste: AddressPasteResult, text: string): string | undefined {
  if (text.trim().length === 0) {
    return undefined;
  }
  if (text.length > MAXIMUM_PASTE_LENGTH) {
    return ADDRESS_PASTE_TOO_LONG;
  }
  if (paste.proposal === undefined) {
    return 'Keine Anschrift erkannt. Die Felder sind unverändert.';
  }
  const rest = addressPasteRest(paste).length;
  return [
    'Anschrift erkannt und in die Felder übernommen. Prüfe sie und wähle den Zweck.',
    paste.proposal.countryCode === '' ? 'Das Land ist noch offen.' : undefined,
    rest === 0 ? undefined : rest === 1 ? 'Eine Angabe wurde nicht übernommen, sie steht unter dem Feld.' : `${rest} Angaben wurden nicht übernommen, sie stehen unter dem Feld.`,
  ]
    .filter((satz) => satz !== undefined)
    .join(' ');
}

export const ADDRESS_PASTE_KEPT = 'Felder, die du selbst geändert hast, bleiben stehen.';
export const ADDRESS_PASTE_REST_HEADING = 'Nicht übernommen';
export const ADDRESS_PASTE_REST_REASON = 'wurde nicht als Teil der Anschrift erkannt';
